import { useState } from 'react';
import { useTheme } from '../../context/ThemeContext.jsx';
import PreviewTab from './PreviewTab.jsx';
import WaterfallTab from './WaterfallTab.jsx';
import GlyphMapTab from './GlyphMapTab.jsx';
import OpenTypeTab from './OpenTypeTab.jsx';
import VariableTab from './VariableTab.jsx';
import StarRating from '../StarRating.jsx';
import SimilarFonts from '../SimilarFonts.jsx';
import FontPairings from '../FontPairings.jsx';
import LicenseSummary from '../LicenseSummary.jsx';

const TABS = [
  { id: 'preview', label: 'preview' },
  { id: 'waterfall', label: 'waterfall' },
  { id: 'glyphs', label: 'glyphs' },
  { id: 'opentype', label: 'ot features' },
  { id: 'variable', label: 'variable', requiresVariable: true },
];

export default function DetailPanel({
  font, onClose, previewText, setPreviewText,
  toggleActivation, deleteFont, tags, onAddTag, onRemoveTag,
  showDetailTagDropdown, setShowDetailTagDropdown,
  collections, onSelectFont, onRate, onDownload,
}) {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('preview');

  if (!font) return null;

  const isVariable = font.is_variable || (font.variable_axes && (typeof font.variable_axes === 'string' ? JSON.parse(font.variable_axes) : font.variable_axes)?.length > 0);
  const visibleTabs = TABS.filter(t => !t.requiresVariable || isVariable);

  const renderTab = () => {
    switch (activeTab) {
      case 'preview': return <PreviewTab font={font} previewText={previewText} setPreviewText={setPreviewText} />;
      case 'waterfall': return <WaterfallTab font={font} previewText={previewText} />;
      case 'glyphs': return <GlyphMapTab font={font} />;
      case 'opentype': return <OpenTypeTab font={font} previewText={previewText} />;
      case 'variable': return <VariableTab font={font} previewText={previewText} />;
      default: return null;
    }
  };

  return (
    <aside style={{
      width: 360, borderLeft: `2px solid ${theme.text}`,
      padding: 20, overflowY: 'auto',
      background: theme.bgElevated,
      height: '100%', boxSizing: 'border-box',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: 12, paddingBottom: 12, borderBottom: `2px solid ${theme.text}`,
      }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: theme.textFaint }}>
            {font.category}{font.license_type && font.license_type !== 'unknown' ? ` · ${font.license_type}` : ''}
          </div>
          <h3 style={{ fontFamily: "'Aeonik', Arial, Helvetica, sans-serif", fontWeight: 450, fontSize: 24, margin: '6px 0 0', color: theme.text, letterSpacing: '0.01em' }}>{font.name}</h3>
        </div>
        <span
          onClick={onClose}
          style={{ fontSize: 13, color: theme.textFaint, cursor: 'pointer', lineHeight: 1, padding: '2px 4px' }}
        >✕</span>
      </div>

      {/* Star Rating */}
      <div style={{ marginTop: 12, marginBottom: 4 }}>
        <StarRating
          rating={font.user_rating}
          avgRating={font.avg_rating}
          ratingCount={font.rating_count}
          onRate={(r) => onRate?.(font.id, r)}
          size={16}
          interactive
        />
      </div>

      {/* Tab Bar */}
      <div style={{
        display: 'flex', gap: 14, marginTop: 16, marginBottom: 16,
        borderBottom: `1px solid ${theme.border}`, overflowX: 'auto',
      }}>
        {visibleTabs.map(tab => (
          <span
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              fontSize: 12, textTransform: 'lowercase', whiteSpace: 'nowrap',
              paddingBottom: 8, marginBottom: -1, cursor: 'pointer',
              color: activeTab === tab.id ? theme.text : theme.textTertiary,
              borderBottom: `2px solid ${activeTab === tab.id ? theme.accent : 'transparent'}`,
            }}
          >{tab.label}</span>
        ))}
      </div>

      {/* Active Tab Content */}
      <div style={{ marginBottom: 20 }}>
        {renderTab()}
      </div>

      {/* Metadata */}
      <div style={{ marginBottom: 20 }}>
        <div style={{
          fontSize: 10, fontWeight: 600, color: theme.textFaint,
          marginBottom: 8, letterSpacing: '0.14em', textTransform: 'uppercase',
          paddingBottom: 8, borderBottom: `1px solid ${theme.border}`,
        }}>details</div>
        {[
          ['designer', font.designer],
          ['category', font.category],
          ['styles', `${font.style_count} styles`],
          ['license', font.license_type !== 'unknown' ? font.license_type : font.license],
          ['file size', font.size_display],
          ['source', font.source],
          font.license_seats && ['seats', font.license_seats],
          font.license_expiry && ['expires', new Date(font.license_expiry).toLocaleDateString()],
        ].filter(Boolean).filter(([_, v]) => v).map(([k, v]) => (
          <div key={k} style={{
            display: 'flex', justifyContent: 'space-between',
            fontSize: 12, padding: '7px 0',
            borderBottom: `1px solid ${theme.borderLight}`,
          }}>
            <span style={{ color: theme.textFaint, textTransform: 'lowercase' }}>{k}</span>
            <span style={{ color: theme.textSecondary }}>{v}</span>
          </div>
        ))}
      </div>

      {/* Font Files */}
      {font.files?.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{
            fontSize: 10, fontWeight: 600, color: theme.textFaint,
            marginBottom: 8, letterSpacing: '0.14em', textTransform: 'uppercase',
            paddingBottom: 8, borderBottom: `1px solid ${theme.border}`,
          }}>files ({font.files.length})</div>
          {font.files.map(f => (
            <div key={f.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontSize: 11, padding: '6px 0',
              borderBottom: `1px solid ${theme.borderLight}`,
            }}>
              <span style={{ color: theme.textTertiary }}>
                {f.weight} {f.style} <span style={{ color: theme.textGhost }}>.{f.format}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Tags */}
      <div style={{ marginBottom: 20 }}>
        <div style={{
          fontSize: 10, fontWeight: 600, color: theme.textFaint,
          marginBottom: 8, letterSpacing: '0.14em', textTransform: 'uppercase',
          paddingBottom: 8, borderBottom: `1px solid ${theme.border}`,
        }}>tags</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingTop: 10 }}>
          {(font.tags || []).map(t => {
            const isAuto = t.is_auto || t.source === 'auto';
            return (
              <span key={t.id} style={{
                fontSize: 11, letterSpacing: '0.04em',
                color: theme.text,
                padding: '3px 8px', borderRadius: 0,
                border: `1px ${isAuto ? 'dashed' : 'solid'} ${theme.borderMedium}`,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {isAuto && <span style={{ fontSize: 8, opacity: 0.6, fontWeight: 700, textTransform: 'uppercase' }}>auto</span>}
                {t.name}
                <span
                  onClick={() => onRemoveTag(t.id)}
                  style={{ cursor: 'pointer', fontSize: 10, color: theme.textFaint }}
                >{'✕'}</span>
              </span>
            );
          })}
          <div style={{ position: 'relative' }}>
            <span
              onClick={() => setShowDetailTagDropdown(!showDetailTagDropdown)}
              style={{
                fontSize: 11, letterSpacing: '0.04em', color: theme.accent,
                border: `1px dashed ${theme.accent}`, padding: '3px 8px',
                cursor: 'pointer',
              }}
            >+ add</span>
            {showDetailTagDropdown && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, marginTop: 4,
                background: theme.bgElevated, border: `1px solid ${theme.borderHover}`,
                borderRadius: 0, padding: 4, minWidth: 140, zIndex: 60,
                boxShadow: theme.shadow,
              }}>
                {tags.filter(t => !(font.tags || []).some(ft => ft.id === t.id)).length === 0 && (
                  <div style={{ padding: '6px 10px', fontSize: 11, color: theme.textFaint }}>
                    {tags.length === 0 ? 'No tags created' : 'All tags assigned'}
                  </div>
                )}
                {tags.filter(t => !(font.tags || []).some(ft => ft.id === t.id)).map(t => (
                  <button
                    key={t.id}
                    onClick={() => onAddTag(t.id)}
                    style={{
                      width: '100%', padding: '6px 10px', background: 'none',
                      border: 'none', borderRadius: 0, color: theme.textSecondary,
                      fontSize: 11, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = theme.bgHover}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: t.color }} />
                    {t.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Collections */}
      <div style={{ marginBottom: 20 }}>
        <div style={{
          fontSize: 10, fontWeight: 600, color: theme.textFaint,
          marginBottom: 8, letterSpacing: '0.14em', textTransform: 'uppercase',
          paddingBottom: 8, borderBottom: `1px solid ${theme.border}`,
        }}>collections</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingTop: 10 }}>
          {(font.collections || []).map(c => (
            <span key={c.name || c} style={{
              fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.textFaint,
            }}>{c.name || c}</span>
          ))}
        </div>
      </div>

      {/* Similar Fonts */}
      <SimilarFonts fontId={font.id} onSelectFont={onSelectFont} />

      {/* Font Pairings */}
      <FontPairings fontId={font.id} onSelectFont={onSelectFont} />

      {/* AI License Summary */}
      <LicenseSummary fontId={font.id} license={font.license} licenseType={font.license_type} />

      {/* Actions */}
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex' }}>
          <button
            onClick={() => toggleActivation(font.id)}
            style={{
              flex: 1, height: 42,
              background: font.is_activated ? 'transparent' : theme.fill,
              border: font.is_activated ? `1px solid ${theme.borderMedium}` : 'none',
              borderRadius: 0,
              color: font.is_activated ? theme.text : theme.fillFg,
              fontSize: 13, textTransform: 'lowercase', textAlign: 'left', padding: '0 14px',
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >{font.is_activated ? 'deactivate font' : 'activate font'}</button>
          <span
            onClick={() => toggleActivation(font.id)}
            style={{
              height: 42, width: 42, background: theme.accent,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: theme.accentFg, fontSize: 12,
            }}
          >▶</span>
        </div>

        <span
          onClick={() => onDownload?.(font.id)}
          style={{ fontSize: 13, color: theme.accent, cursor: 'pointer', textTransform: 'lowercase' }}
        >download original files</span>

        <span
          onClick={() => { if (confirm(`Delete ${font.name}?`)) deleteFont(font.id); }}
          style={{ fontSize: 13, color: theme.textFaint, cursor: 'pointer', textTransform: 'lowercase' }}
        >delete font</span>
      </div>
    </aside>
  );
}
